/**
 * Doc view pagination.
 *
 * The doc view is a stack of fixed A4 pages, so something has to decide which
 * block lands on which page. Pure functions, no DOM: `buildDocItems` flattens a
 * briefing into the order it prints in, the caller measures each item once at the
 * page's content width, and `paginate` assigns them to pages from those heights.
 *
 * Keeping the rules here rather than inside the component is what makes them
 * testable — in jsdom every height is 0, so a test that renders the component can
 * only ever see one page. The invariants below are asserted against injected
 * heights in `paginate.test.ts`; the real height measurement needs a browser.
 */
import type { Briefing, Exhibit, SectionId, TimelineData } from './types';

/** Hide generated exhibits superseded by a more decision-bearing presentation.
 * Old relationship paths and the value-exchange arrows duplicate Q3; the
 * page-sized monthly count chart duplicates relationship evidence. */
export function isRedundantGeneratedExhibit(exhibit: Exhibit): boolean {
  if (exhibit.id === 'existing-lp-value-exchange') return true;
  if (exhibit.kind === 'flowchart') return true;
  if (exhibit.kind !== 'timeline') return false;
  const timeline = exhibit.data as TimelineData;
  return timeline.unit === 'messages per month'
    || exhibit.title === 'Email activity shows the cadence of this LP relationship';
}

export type DocItem =
  | { kind: 'lede' }
  | { kind: 'dek' }
  | { kind: 'kpis' }
  | { kind: 'flags' }
  /** `sectionId` is set when the exhibit prints inside a section, so a page that
   *  opens on it is a continuation of that section rather than a fresh start. */
  | { kind: 'exhibit'; exhibitId: string; sectionId?: SectionId }
  | { kind: 'section-header'; sectionId: SectionId }
  | { kind: 'block'; sectionId: SectionId; blockIndex: number }
  | { kind: 'sources-header' }
  | { kind: 'sources' };

/**
 * The print order. An exhibit prints where its `exhibit_ref` block sits; an
 * exhibit nothing refers to prints in the summary, under the flags — that is where
 * the mockup puts Exhibit 1, which no section references.
 */
export function buildDocItems(briefing: Briefing): DocItem[] {
  const referenced = new Set<string>();
  for (const section of briefing.sections) {
    for (const block of section.blocks) {
      if (block.type === 'exhibit_ref') referenced.add(block.exhibit_id);
    }
  }

  const items: DocItem[] = [{ kind: 'lede' }, { kind: 'dek' }, { kind: 'kpis' }];
  if (briefing.summary.flags.length) items.push({ kind: 'flags' });
  for (const exhibit of briefing.exhibits) {
    if (!referenced.has(exhibit.id) && !isRedundantGeneratedExhibit(exhibit)) {
      items.push({ kind: 'exhibit', exhibitId: exhibit.id });
    }
  }

  for (const section of briefing.sections) {
    items.push({ kind: 'section-header', sectionId: section.id });
    section.blocks.forEach((block, blockIndex) => {
      if (block.type === 'exhibit_ref') {
        // q2 already has the older evidence table. Some persisted documents
        // also reference an exhibit there; do not reserve a blank PDF slot for
        // the duplicate that BriefingView deliberately suppresses.
        if (section.id === 'q2') return;
        // Only if it resolves. A dangling ref is a generator bug the validator
        // catches; it must not put an empty slot on the page.
        if (briefing.exhibits.some(e => e.id === block.exhibit_id && !isRedundantGeneratedExhibit(e))) {
          items.push({ kind: 'exhibit', exhibitId: block.exhibit_id, sectionId: section.id });
        }
        return;
      }
      items.push({ kind: 'block', sectionId: section.id, blockIndex });
    });
  }

  const publicSources = briefing.sources.filter(s => s.kind === 'public');
  if (publicSources.length) {
    items.push({ kind: 'sources-header' });
    // The printed list flows into columns. Measuring each row on its own counts
    // 21 rows as 21 full-width lines and can create a phantom extra page.
    // Relationship records remain in the web view, where they are openable.
    items.push({ kind: 'sources' });
  }
  return items;
}

/** Items that may never be broken across a page boundary, whatever their height.
 *  A table, a tile row and an exhibit are each one figure; splitting one makes
 *  both halves unreadable. Everything else here is a single short run of text. */
const ATOMIC: ReadonlySet<DocItem['kind']> = new Set<DocItem['kind']>([
  'lede', 'dek', 'kpis', 'flags', 'exhibit', 'section-header', 'block',
  'sources-header', 'sources',
]);

/**
 * Greedy fill, with one lookahead rule: a section header may not be the last
 * thing on a page. A header whose first block does not fit beside it moves to the
 * next page with that block, so no reader ever turns a page to find the answer to
 * a question they just read.
 *
 * Returns item indices grouped per page. An item taller than a whole page gets a
 * page to itself rather than being dropped or clipped.
 */
export function paginate(
  items: DocItem[],
  heightOf: (index: number) => number,
  budget: number,
): number[][] {
  if (!items.length) return [];
  const pages: number[][] = [];
  let page: number[] = [];
  let used = 0;

  const flush = () => {
    if (page.length) pages.push(page);
    page = [];
    used = 0;
  };

  for (let i = 0; i < items.length; i++) {
    if (!ATOMIC.has(items[i].kind)) {
      // Defensive: every kind is atomic today. A future splittable kind must
      // arrive with its own splitting rule rather than silently overflowing.
      throw new Error(`briefing pagination: ${items[i].kind} has no split rule`);
    }
    let needed = heightOf(i);
    // A header is placed together with whatever follows it on the same page.
    if ((items[i].kind === 'section-header' || items[i].kind === 'sources-header')
      && i + 1 < items.length) {
      needed += heightOf(i + 1);
    }
    if (page.length && used + needed > budget) flush();
    page.push(i);
    used += heightOf(i);
  }
  flush();
  return pages;
}

/**
 * The section a page continues from, or null when the page starts cleanly. The
 * renderer uses it to print a section's content without repeating its number and
 * question — a reader sees one numbered header per section, not one per page.
 */
export function continuationOf(items: DocItem[], page: number[]): SectionId | null {
  const first = items[page[0]];
  if (!first) return null;
  if (first.kind === 'block') return first.sectionId;
  if (first.kind === 'exhibit') return first.sectionId ?? null;
  return null;
}
